/**
 * Credit Accounts Page.
 *
 * Displays all credit/liability accounts with their balances, limits,
 * and recent transactions.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Plus, TrendingUp, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import {
  useCreditAccounts,
  useCreateCreditAccount,
  useCreditTransactions,
  useRecordPayment,
} from "@/features/finance/api";
import { ACCOUNT_TYPES } from "@/types/credit-account";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

function CreditAccountsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useCreditAccounts();
  const createMutation = useCreateCreditAccount();

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("line_of_credit");
  const [creditLimit, setCreditLimit] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [compoundingMethod, setCompoundingMethod] = useState("daily");
  const [provider, setProvider] = useState("");

  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");

  const { data: transactions } = useCreditTransactions(
    selectedAccount || undefined,
  );
  const paymentMutation = useRecordPayment(selectedAccount || "");

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  const formatPercent = (v: number) => `${(v * 100).toFixed(2)}%`;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await createMutation.mutateAsync({
      name,
      account_type: accountType,
      credit_limit: creditLimit ? parseFloat(creditLimit) : undefined,
      interest_rate: interestRate ? parseFloat(interestRate) / 100 : undefined,
      compounding_method: compoundingMethod,
      provider: provider || undefined,
    });
    setShowCreate(false);
    setName("");
    setCreditLimit("");
    setInterestRate("");
    setProvider("");
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || !paymentAmount) return;
    await paymentMutation.mutateAsync({
      amount: parseFloat(paymentAmount),
      description: "Manual payment",
    });
    setPaymentAmount("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Credit Accounts
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage lines of credit, loans, and credit cards.
          </p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          <Plus className="h-4 w-4" /> Add Account
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              New Credit Account
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="Account Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g., Farm Credit LOC"
              />
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Type
                </label>
                <select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
                >
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder="Bank name"
              />
              <Input
                label="Credit Limit"
                type="number"
                step="0.01"
                min="0"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                placeholder="0.00"
              />
              <Input
                label="Interest Rate (%)"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                placeholder="7.25"
              />
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Compounding
                </label>
                <select
                  value={compoundingMethod}
                  onChange={(e) => setCompoundingMethod(e.target.value)}
                  className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
                >
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                  <option value="none">Simple (no compounding)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Create
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : data && data.items.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {data.items.map((acct) => {
            const utilization = acct.credit_limit
              ? (acct.current_balance / acct.credit_limit) * 100
              : 0;
            const isSelected = selectedAccount === acct.id;

            return (
              <Card
                key={acct.id}
                padding="none"
                className={`cursor-pointer hover-lift ${isSelected ? "ring-2 ring-[var(--color-interactive-primary)]" : ""}`}
                onClick={() => navigate(`/credit-accounts/${acct.id}`)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-[var(--text-primary)]">
                        {acct.name}
                      </h3>
                      <p className="text-xs text-[var(--text-secondary)]">
                        {acct.provider || acct.account_type.replace("_", " ")}
                      </p>
                    </div>
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Badge
                        variant={
                          acct.current_balance > 0 ? "warning" : "success"
                        }
                      >
                        {formatCurrency(acct.current_balance)}
                      </Badge>
                      <button
                        onClick={() => setDeleteTarget(acct.id)}
                        className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-[var(--text-secondary)]">
                        Limit
                      </p>
                      <p className="text-sm font-medium">
                        {acct.credit_limit
                          ? formatCurrency(acct.credit_limit)
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-secondary)]">
                        Rate
                      </p>
                      <p className="text-sm font-medium">
                        {formatPercent(acct.interest_rate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-secondary)]">
                        Utilization
                      </p>
                      <p className="text-sm font-medium">
                        {acct.credit_limit
                          ? `${utilization.toFixed(1)}%`
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Utilization Bar */}
                  {acct.credit_limit && (
                    <div className="mt-3 h-2 rounded-full bg-[var(--surface-sunken)]">
                      <div
                        className={`h-2 rounded-full ${utilization > 80 ? "bg-[var(--color-danger)]" : utilization > 50 ? "bg-[var(--color-warning)]" : "bg-[var(--color-success)]"}`}
                        style={{ width: `${Math.min(utilization, 100)}%` }}
                      />
                    </div>
                  )}

                  {/* Accrued Interest */}
                  {acct.current_balance > 0 && acct.interest_rate > 0 && (
                    <AccruedInterestBadge accountId={acct.id} />
                  )}

                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        setSelectedAccount(isSelected ? null : acct.id)
                      }
                    >
                      {isSelected ? "Hide" : "Transactions"}
                    </Button>
                  </div>
                </div>

                {/* Transactions Panel */}
                {isSelected && (
                  <div className="border-t border-[var(--border-default)] bg-[var(--surface-sunken)] p-4">
                    {/* Payment Form */}
                    <form onSubmit={handlePayment} className="mb-3 flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="Payment amount"
                        className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
                      />
                      <Button
                        type="submit"
                        size="sm"
                        isLoading={paymentMutation.isPending}
                      >
                        Pay
                      </Button>
                    </form>

                    {transactions && transactions.length > 0 ? (
                      <div className="max-h-48 space-y-1 overflow-y-auto">
                        {transactions.map((txn) => (
                          <div
                            key={txn.id}
                            className="flex items-center justify-between rounded bg-[var(--surface-base)] px-3 py-2 text-xs"
                          >
                            <div>
                              <span className="capitalize font-medium">
                                {txn.transaction_type}
                              </span>
                              {txn.description && (
                                <span className="ml-2 text-[var(--text-secondary)]">
                                  {txn.description}
                                </span>
                              )}
                            </div>
                            <span
                              className={
                                txn.transaction_type === "payment"
                                  ? "text-[var(--text-success)]"
                                  : "text-[var(--text-danger)]"
                              }
                            >
                              {txn.transaction_type === "payment" ? "-" : "+"}
                              {formatCurrency(txn.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--text-tertiary)]">
                        No transactions yet.
                      </p>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <CreditCard />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No credit accounts
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Add a line of credit, loan, or credit card to track financing.
            </p>
          </div>
        </Card>
      )}
      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/credit-accounts"
        entityId={deleteTarget || ""}
        queryKey="credit-accounts"
      />
    </div>
  );
}

/**
 * Inline component showing accrued interest and a Post button for a
 * single credit account.
 */
function AccruedInterestBadge({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const { data: accrued } = useQuery<{
    accrued_amount: number;
    days: number;
    daily_rate: number;
    compounding_method: string;
  }>({
    queryKey: ["accrued-interest", accountId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/credit-accounts/${accountId}/accrued-interest`,
        )
      ).data,
  });

  const postMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/credit-accounts/${accountId}/post-interest`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credit-accounts"] });
      qc.invalidateQueries({ queryKey: ["accrued-interest", accountId] });
      qc.invalidateQueries({ queryKey: ["credit-transactions", accountId] });
      toast.success("Interest posted");
    },
    onError: () => toast.error("Failed to post interest"),
  });

  if (!accrued || accrued.accrued_amount <= 0) return null;

  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  return (
    <div className="mt-3 flex items-center justify-between rounded-[var(--radius-md)] bg-[var(--fill-warning)] px-3 py-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-3.5 w-3.5 text-[var(--text-warning)]" />
        <div>
          <p className="text-xs font-medium text-[var(--text-warning)]">
            {fmt(accrued.accrued_amount)} accrued ({accrued.days}d)
          </p>
          <p className="text-[10px] text-[var(--text-secondary)]">
            {fmt(accrued.daily_rate)}/day &middot; {accrued.compounding_method}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => postMutation.mutate()}
        disabled={postMutation.isPending}
        className="text-[10px] font-semibold text-[var(--text-warning)] hover:underline disabled:opacity-50"
      >
        {postMutation.isPending ? "Posting..." : "Post Interest"}
      </button>
    </div>
  );
}

export default CreditAccountsPage;
